<template>
    <div class="patent-details" ref="patentDetailsRef" tabindex="0">
        <div class="nav-tab d-flex flex-column align-items-center">
            <font-awesome-icon
                :icon="`fas fa-xmark`"
                class="pa-2 px-2 grey05 patent-nav close grey02--text"
                v-tooltip="{
                    content: 'Close',
                    placement: 'left',
                    delay: { show: 500, hide: 0 },
                }"
                @click="closeDetails"
                @keydown.enter="closeDetails"
                @keydown.space="closeDetails"
            />
            <div class="divide hor"></div>
            <font-awesome-icon
                :icon="`fas fa-chevron-down`"
                class="pa-2 mb-2 grey05 patent-nav up grey02--text"
                size="sm"
                v-tooltip="{
                    content: 'Previous Patent',
                    placement: 'left',
                    delay: { show: 500, hide: 0 },
                }"
                @click="previousPatent"
                @keydown.enter="previousPatent"
                @keydown.space="previousPatent"
            />
            <font-awesome-icon
                :icon="`fas fa-chevron-down`"
                class="pa-2 grey05 patent-nav down grey02--text"
                v-tooltip="{
                    content: 'Next Patent',
                    placement: 'left',
                    delay: { show: 500, hide: 0 },
                }"
                @click="nextPatent"
                @keydown.enter="nextPatent"
                @keydown.space="nextPatent"
            />
        </div>
        <div class="contain-shadow" ref="containShadowRef">
            <PatentHeader
                ref="highLevelRef"
                :patentDetails="patentViewerStore.patentDetails || {}"
                :allowPruning="portfolioManagerStore.allowPruning"
                :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                :shouldHideInfo="shouldHideInfo"
                :titleEllipsis="titleEllipsis"
                @prune-patent="prunePatent"
            />

            <div
                class="anchor-tabs d-flex align-items-end mb-8 px-4 sticky-tabs"
                ref="anchorTabsRef"
                :class="{ 'top-position': shouldHideInfo }"
            >
                <div
                    v-for="tab in anchorTabs"
                    class="tab px-4 pb-2"
                    :class="{ active: tab.id === activeTab }"
                    @click="scrollTo(tab.id)"
                >
                    <p class="geist-font knights-cloak-40--text semi-bold">{{ tab.title }}</p>
                </div>
            </div>

            <template v-if="showAISummary">
                <PatentAISummary
                    class="mb-4"
                    :patentDetails="patentViewerStore.patentDetails || {}"
                    :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                />
            </template>

            <PatentOverview
                :patentDetails="patentViewerStore.patentDetails || {}"
                :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
            />

            <template v-if="showProductMap">
                <PatentMappedProducts
                    ref="patentProductMapRef"
                    :patentDetails="patentViewerStore.patentDetails || {}"
                    :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                />
            </template>

            <template v-if="showPatentSim">
                <PatentSimilarity
                    ref="patentSimilarityRef"
                    :patentDetails="patentViewerStore.patentDetails || {}"
                    :loadingPatentDetail="patentViewerStore.loadingPatentDetail"
                />
            </template>

            <!-- Accordions -->
            <div class="accor-panel px-4 mb-5">
                <AccordionPanel label="Claims" id="claims">
                    <AonSkeleton
                        class="w-full d-flex align-items-center mb-1"
                        :loading="patentViewerStore.loadingPatentDetail"
                        :data="0"
                        :height="'80px'"
                        light
                    >
                        <p class="py-4" v-html="patentViewerStore.patentDetails?.claims"></p>
                        <p v-if="!patentViewerStore.patentDetails?.claims">No claims found</p>
                    </AonSkeleton>
                </AccordionPanel>
            </div>
            <div class="accor-panel px-4 mb-5">
                <AccordionPanel label="Description" id="description">
                    <AonSkeleton
                        class="w-full d-flex align-items-center mb-1"
                        :loading="patentViewerStore.loadingPatentDetail"
                        :data="0"
                        :height="'80px'"
                        light
                    >
                        <p class="py-4" v-html="patentViewerStore.patentDetails?.description"></p>
                        <p v-if="!patentViewerStore.patentDetails?.description">
                            No description found
                        </p>
                    </AonSkeleton>
                </AccordionPanel>
            </div>
            <div class="accor-panel px-4 mb-5">
                <AccordionPanel label="Patent Family Members" id="family">
                    <AonSkeleton
                        class="w-full d-flex align-items-center mb-1 flex-wrap"
                        :loading="patentViewerStore.loadingPatentDetail"
                        :data="0"
                        :height="'80px'"
                        light
                    >
                        <div
                            v-for="patent in patentViewerStore.patentDetails?.familyPatents"
                            :key="patent.patentId"
                            class="slide-item mt-2 d-flex justify-content-center w-25"
                            @click="goToGooglePatents(patent.googlePatentsLink)"
                        >
                            <p class="link bold">
                                {{ patent.patentId }}
                            </p>
                        </div>
                    </AonSkeleton>
                </AccordionPanel>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, onUnmounted, ref, watch } from 'vue'
import { usePatentViewerStore, usePortfolioManagerStore } from '@/stores'

import PatentHeader from '@/components/patentViewer/patentDetailComponents/PatentHeader.vue'
import PatentOverview from '@/components/patentViewer/patentDetailComponents/PatentOverview.vue'
import PatentMappedProducts from '@/components/patentViewer/patentDetailComponents/PatentMappedProducts.vue'
import PatentSimilarity from '@/components/patentViewer/patentDetailComponents/PatentSimilarity.vue'
import PatentAISummary from '@/components/patentViewer/patentDetailComponents/PatentAISummary.vue'

import AccordionPanel from '@/components/helpers/AccordionPanel.vue'

import { debounce } from 'lodash'
import { useFlag } from '@unleash/proxy-client-vue'

const patentViewerStore = usePatentViewerStore()
const portfolioManagerStore = usePortfolioManagerStore()

const showProductMap = useFlag('ipAlpha.AAPatentProductMap')
const showPatentSim = useFlag('ipAlpha.AAPatentSim')
const showAISummary = useFlag('ipAlpha.AAPatentAISummary')
const eventBus = inject('eventBus')

const patentDetailsRef = ref(null)
const patentSimilarityRef = ref(null)
const patentProductMapRef = ref(null)

const activeTab = ref('overview')

// Sticky functionality
const titleEllipsis = ref(false)
const containShadowRef = ref(null)
const highLevelRef = ref(null)
const anchorTabsRef = ref(null)
const lastScrollTop = ref(0)
const shouldHideInfo = ref(false)

onMounted(() => {
    window.addEventListener('keydown', handleKeyPress)
    if (containShadowRef.value) {
        containShadowRef.value.addEventListener('scroll', handleScroll)
    }
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyPress)
    if (containShadowRef.value) {
        containShadowRef.value.removeEventListener('scroll', handleKeyPress)
    }
})

watch(
    () => patentViewerStore.targetPatentId,
    (newVal, oldVal) => {
        if (!newVal) {
            patentViewerStore.patentDetailsOpen = false
            return
        }

        if (newVal !== oldVal) {
            patentViewerStore.patentDetailsOpen = true
        }
    }
)

watch(
    () => patentViewerStore.patentDetailsOpen,
    (isOpen) => {
        if (isOpen) {
            patentDetailsRef.value?.focus()
        } else {
            patentViewerStore.patentDetails = null
        }
    }
)

const anchorTabs = computed(() => {
    let tabs = [{ title: 'Overview', id: 'overview' }]

    if (showPatentSim.value) {
        tabs.push({ title: 'Similar Patents', id: 'similar' })
    }

    return tabs
})

const updateActiveTabOnScroll = () => {
    if (!containShadowRef.value) return

    // Get all section elements that correspond to tabs
    const sections = anchorTabs.value.map((tab) => document.getElementById(tab.id))

    // Get the height of sticky elements to use as offset
    const headerHeight = highLevelRef.value?.highLevelRef?.offsetHeight || 0
    const tabsHeight = anchorTabsRef.value?.offsetHeight || 0
    const offset = headerHeight + tabsHeight + 20 // Adding extra padding

    // Get current scroll position
    const scrollPosition = containShadowRef.value.scrollTop + offset

    // Check if we're at the bottom of the scroll container
    const isAtBottom =
        containShadowRef.value.scrollHeight - containShadowRef.value.scrollTop <=
        containShadowRef.value.clientHeight + 5 // 5px tolerance

    // If at bottom, prioritize the last tab that would be visible
    if (isAtBottom && sections.length > 0) {
        const lastVisibleTabIndex = sections.reduce((lastIndex, section, index) => {
            if (section) return index
            return lastIndex
        }, 0)

        if (activeTab.value !== anchorTabs.value[lastVisibleTabIndex].id) {
            activeTab.value = anchorTabs.value[lastVisibleTabIndex].id
        }
        return
    }

    // Normal tab detection logic for non-bottom scroll positions
    for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i]
        if (section && section.offsetTop <= scrollPosition) {
            if (activeTab.value !== anchorTabs.value[i].id) {
                activeTab.value = anchorTabs.value[i].id
            }
            break
        }
    }
}

const handleScroll = debounce(() => {
    if (!containShadowRef.value) return
    const scrollTop = containShadowRef.value.scrollTop

    if (scrollTop > 50) {
        titleEllipsis.value = true
    } else {
        titleEllipsis.value = false
    }

    // Determine scroll direction
    if (scrollTop > lastScrollTop.value && scrollTop > 50) {
        shouldHideInfo.value = true
    } else if (scrollTop < lastScrollTop.value || scrollTop <= 50) {
        shouldHideInfo.value = false
    }

    lastScrollTop.value = scrollTop

    updateActiveTabOnScroll()
}, 10)

const scrollTo = (index) => {
    const element = document.getElementById(index)
    if (element && containShadowRef.value) {
        activeTab.value = index

        if (index === 'similar') {
            // Check if similarity has been run
            if (
                patentSimilarityRef.value &&
                patentViewerStore.patentDetails?.publicationPk &&
                !patentViewerStore.similarPatentsExistInCache(
                    patentViewerStore.patentDetails.publicationPk
                )
            ) {
                patentSimilarityRef.value.onFindSimilarPatents()
            }
        }

        // Get the height of sticky elements to use as offset
        const headerHeight = highLevelRef.value?.offsetHeight || 0
        const offset = headerHeight
        const elementPosition = element.offsetTop - 140

        // Only scroll if not already at the target position
        if (Math.abs(containShadowRef.value.scrollTop - elementPosition) > 10) {
            containShadowRef.value.scrollTo({
                top: elementPosition,
                behavior: 'smooth',
            })
        }
    }
}

const previousPatent = () => {
    eventBus.emit('previous-patent')
}

const nextPatent = () => {
    eventBus.emit('next-patent')
}

const closeDetails = async () => {
    eventBus.emit('close-patent')
    patentViewerStore.patentDetailsOpen = false
}

const handleKeyPress = (event) => {
    if (!patentViewerStore.patentDetailsOpen) return

    if (!patentDetailsRef.value?.contains(document.activeElement)) return

    switch (event.key) {
        case 'ArrowUp':
            previousPatent()
            break
        case 'ArrowDown':
            nextPatent()
            break
    }
}

const goToGooglePatents = (googleLink) => {
    window.open(`${googleLink}`, '_blank')
}

const prunePatent = async () => {
    if (!patentViewerStore.patentDetails) return
    patentViewerStore.patentDetails.pruned = !patentViewerStore.patentDetails.pruned
    eventBus.emit('prune-patent', patentViewerStore.patentDetails)
}
</script>

<style lang="scss" scoped>
@forward '@/styles/new_styles.scss';

.patent-details {
    width: 1000px;
    height: 100%;
    background-color: white;
    box-shadow: -8px 0px 76px 0px rgba(0, 0, 0, 0.25);

    position: absolute;
    z-index: 12;
    right: 0;
    top: 0;

    &:focus {
        outline: none;
    }

    .abstract-holder {
        min-height: 80px;
    }

    .prune-button {
        background: vars.$grey07;
        padding: 10px 16px;
        border-radius: 8px;

        transition: all 0.2s ease-in-out;

        &:hover {
            background: vars.$grey05;
        }
    }

    :deep(.not-pruned) path {
        fill: vars.$grey04 !important;
    }

    :deep(.pruned) path {
        fill: vars.$success !important;
    }

    .accor-panel {
        p {
            white-space: pre-line;
        }

        .accordion {
            border-bottom: 1px solid vars.$grey03;

            &:first-of-type {
                border-top: 1px solid vars.$grey03;
            }
        }
    }

    .nav-tab {
        padding: 10px;
        background: white;
        border-radius: 8px 0 0 8px;
        overflow: hidden;

        position: absolute;
        z-index: -1;
        top: 0;
        left: -58px;

        box-shadow: rgba(0, 0, 0, 0.1) -10px 6px 20px -4px;

        .patent-nav {
            width: 18px;
            height: 18px;
            cursor: pointer;
            border-radius: 4px;
            // border: solid 1px vars.$grey03;
            padding: 10px !important;

            transition: all 0.3s ease-in-out;

            &:hover {
                background-color: vars.$grey04;
            }

            &.up {
                transform: rotate(180deg);
            }
        }
    }

    .anchor-tabs {
        border-bottom: 1px solid rgba(#5e7394, 0.2);
        min-height: 58px;

        .tab {
            border-bottom: solid 5px transparent;
            cursor: pointer;

            &.active {
                border-color: vars.$gold;
            }
        }
    }

    .contain-shadow {
        width: 100%;
        height: 100vh;
        overflow-y: auto;
        overflow-x: hidden;

        position: relative;
    }

    .no-wrap {
        white-space: nowrap;
    }

    .divide {
        border-radius: 50%;
        width: 5px;
        height: 5px;
        background-color: vars.$grey04;
        margin: 0 12px;

        &.hor {
            border-radius: 0;
            width: 25px;
            height: 2px;
            margin: 12px 0;
        }
    }

    .sticky-header {
        position: sticky;
        top: 0;
        z-index: 10;
        background-color: white;

        &.expanded {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
    }

    .sticky-tabs {
        position: sticky;
        top: 76px;
        z-index: 9;
        background-color: white;

        &.top-position {
            top: 76px;
            z-index: 10;
        }
    }

    .assignee,
    .dates {
        height: 40px;
        transform-origin: top;

        &.hide-info {
            opacity: 0;
            transform: scaleY(0);
            position: absolute;
            pointer-events: none;
        }
    }

    .info-spacer {
        height: 0;

        &.active {
            height: 96px;
        }
    }

    .status-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
    }
}
</style>
